import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, TextField, Autocomplete, Select, MenuItem, Alert, Divider } from '@mui/material';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { canSelectUserRegions } from '../../utils/regionManagementRights';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import Iconify from '../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../css/users/addUserDialog.css';

export const AddUserDialoge = ({ openAddUser, setOpenAddUser, setUserId, users, setUsers, totalUsers, setTotalUsers, filteredUsersCount, setFilteredUsersCount }) => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const authenticatedUser = useSelector(state => state.AuthenticatedUser);

    const [roles, setRoles] = useState([]);
    const [regions, setRegions] = useState([]);
    const [sites, setSites] = useState([]);
    const [poles, setPoles] = useState([]);
    const [metiers, setMetiers] = useState([]);
    const [errMsg, setErrMsg] = useState('');

    const formik = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            role_id: '',
            selectedRegion: authenticatedUser.user_region_ids.length === 1 ? [authenticatedUser.user_region_ids[0]] : [],
            selectedSite: [],
            selectedPole: [],
            selectedMetier: [],

        },
        validationSchema: Yup.object().shape({
            first_name: Yup.string().required(t('isRequired')),
            last_name: Yup.string().required(t('isRequired')),
            role_id: Yup.string().required(t('isRequired')),
            email: Yup.string().email(t('emailFormatRequired')).required(t('emailRequied')),
            selectedRegion: Yup.array()
                .min(1, t('Le champ région doit contenir au moins un élément'))
                .required(t('Le champ région doit contenir au moins un élément')),
        }),
        onSubmit: async (values) => {
            try {
                const userData = {
                    first_name: values.first_name,
                    last_name: values.last_name,
                    email: values.email,
                    role_id: values.role_id,
                    regions: values.selectedRegion,
                    sites: values.selectedSite,
                    poles: values.selectedPole,
                    metiers: values.selectedMetier,
                };

                const response = await axios.post('user', { userData });
                if (response.status === 200 || response.status === 201) {
                    setUserId(response.data.id);
                    setUsers([...users, response.data]);
                    setTotalUsers(totalUsers + 1);
                    setFilteredUsersCount(filteredUsersCount + 1);
                    setOpenAddUser(false);
                    setErrMsg('');
                    navigate(`utilisateur_details/${response.data.id}`);
                }
            } catch (error) {
                setErrMsg(error.message || 'erreur lors de l\'ajout de l\'utilisateur')
                console.error('Error adding user:', error);
                CustomErrorLogger(error, 'error adding user in AddUserDialoge');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } = formik;

    const handleClose = useCallback(() => {
        setOpenAddUser(false);
        setErrMsg('')
    }, [setOpenAddUser]);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await axios.get('role');
            if (response.status === 200 || response.status === 201) {
                setRoles(response.data);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            CustomErrorLogger(error, 'error fetching roles in AddUserDialoge');
        }
    }, []);

    const fetchRegions = useCallback(async () => {
        try {
            const response = await axios.get('region');
            if (response.status === 200 || response.status === 201) {
                setRegions(response.data);
            }
        } catch (error) {
            console.error('Error fetching regions:', error);
            CustomErrorLogger(error, 'error fetching regions in AddUserDialoge');
        }
    }, []);

    const fetchSites = useCallback(async () => {
        if (values?.selectedRegion?.length === 0) {
            setSites([]);
            setPoles([]);
            setMetiers([]);
            return;
        }
        try {
            const response = await axios.get('site', { params: { regions: values.selectedRegion } });
            if (response.status === 200 || response.status === 201) {
                setSites(response.data);
            }
        } catch (error) {
            console.error('Error Fetching sites:', error);
            CustomErrorLogger(error, 'error fetching sites in AddUserDialoge');
        }
    }, [values.selectedRegion])

    const fetchPoles = useCallback(async () => {
        if (values?.selectedSite?.length === 0) {
            setPoles([]);
            setMetiers([]);
            return;
        }
        try {
            const response = await axios.get('pole', { params: { sites: values.selectedSite } });
            if (response.status === 200 || response.status === 201) {
                setPoles(response.data);
            }
        } catch (error) {
            console.error('Error Fetching poles:', error);
            CustomErrorLogger(error, 'error fetching poles in AddUserDialoge');
        }
    }, [values.selectedSite]);

    const fetchMetiers = useCallback(async () => {
        if (values?.selectedSite?.length === 0) {
            setMetiers([]);
            return;
        }
        try {
            const response = await axios.get('metier', { params: { poles: values.selectedPole, sites: values.selectedSite } });
            if (response.status === 200 || response.status === 201) {
                setMetiers(response.data);
            }
        } catch (error) {
            console.error('Error Fetching metiers:', error);
            CustomErrorLogger(error, 'error fetching metiers in AddUserDialoge');
        }
    }, [values.selectedPole, values.selectedSite]);

    return (
        <Dialog open={openAddUser} maxWidth='md'>
            <IconButton onClick={handleClose} className='addUser_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addUser_close_icon' />
            </IconButton>
            <DialogTitle className='addUser_dialog_title'>{t('addUserDialogeTitle')}</DialogTitle>
            <Divider />
            {errMsg && <Alert key={errMsg} severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addUser_dialog_content'>
                    <Typography className='addUser_dialog_SubTitle'>{t('addUserDialogeSubTitle')}</Typography>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Prénom')}</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.first_name}
                            className='addUser_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="first_name"
                            error={touched.first_name && Boolean(errors.first_name)}
                            helperText={touched.first_name && errors.first_name}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Nom')}</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.last_name}
                            className='addUser_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="last_name"
                            error={touched.last_name && Boolean(errors.last_name)}
                            helperText={touched.last_name && errors.last_name}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Email address')}</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.email}
                            className='addUser_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>

                    <Box className='addUser_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Role')}</FormHelperText>
                        <Select
                            className={`addUser_select_metier`}
                            value={values.role_id}
                            onChange={(e) => {
                                handleChange(e);
                                setFieldValue('role_id', e.target.value);
                            }}
                            onBlur={handleBlur}
                            onOpen={fetchRoles}
                            name="role"
                            error={touched.role_id && Boolean(errors.role_id)}
                        >
                            {roles && roles?.map((role) => (
                                <MenuItem
                                    key={role.id} className='addUser_select_metier_item' value={role.id}>
                                    {role.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {touched.role_id && errors.role_id && (
                            <FormHelperText error className='addUser_select_helper'>{errors.role_id}</FormHelperText>
                        )}
                    </Box>

                    {canSelectUserRegions(authenticatedUser) &&
                        <Box className='addUser_helper_selct_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Region')}</FormHelperText>
                            <Autocomplete
                                multiple
                                sx={{ padding: 0 }}
                                options={regions}
                                getOptionLabel={(option) => option.name}
                                disableCloseOnSelect={true}
                                value={regions.filter(region => values.selectedRegion.includes(region.id))}
                                onChange={(event, newValue) => {
                                    const selectedRegionIds = newValue?.map(region => region.id);
                                    setFieldValue('selectedRegion', selectedRegionIds);
                                    setFieldValue('selectedSite', []);
                                    setFieldValue('selectedPole', []);
                                    setFieldValue('selectedMetier', []);
                                }}
                                onBlur={handleBlur}
                                onOpen={fetchRegions}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        className='addUser_InputField'
                                        error={touched.selectedRegion && Boolean(errors.selectedRegion)}
                                        helperText={touched.selectedRegion && errors.selectedRegion}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: {
                                                minHeight: 'fit-content', // Adjust the minimum height as needed
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    }
                    <Box className='addUser_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Site')}</FormHelperText>
                        <Autocomplete
                            multiple
                            sx={{ padding: 0 }}
                            options={sites}
                            getOptionLabel={(option) => option.name}
                            disableCloseOnSelect={true}
                            value={sites.filter(site => values.selectedSite.includes(site.id))}
                            onChange={(event, newValue) => {
                                const selectedSiteIds = newValue?.map(site => site.id);
                                setFieldValue('selectedSite', selectedSiteIds);
                                setFieldValue('selectedPole', []);
                                setFieldValue('selectedMetier', []);
                            }}
                            onBlur={handleBlur}
                            onOpen={fetchSites}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className='addUser_InputField'
                                    error={touched.selectedSite && Boolean(errors.selectedSite)}
                                    helperText={touched.selectedSite && errors.selectedSite}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: {
                                            minHeight: 'fit-content', // Adjust the minimum height as needed
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box className='addUser_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Pole')}</FormHelperText>
                        <Autocomplete
                            multiple
                            sx={{ padding: 0 }}
                            options={poles}
                            getOptionLabel={(option) => option.name}
                            disableCloseOnSelect={true}
                            value={poles.filter(pole => values.selectedPole.includes(pole.id))}
                            onChange={(event, newValue) => {
                                const selectedPoleIds = newValue?.map(pole => pole.id);
                                setFieldValue('selectedPole', selectedPoleIds);
                                setFieldValue('selectedMetier', []);
                            }}
                            onBlur={handleBlur}
                            onOpen={fetchPoles}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className='addUser_InputField'
                                    error={touched.selectedPole && Boolean(errors.selectedPole)}
                                    helperText={touched.selectedPole && errors.selectedPole}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: {
                                            minHeight: 'fit-content',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box className='addUser_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Metier')}</FormHelperText>
                        <Autocomplete
                            multiple
                            sx={{ padding: 0 }}
                            options={metiers}
                            getOptionLabel={(option) => option.name}
                            disableCloseOnSelect={true}
                            value={metiers.filter(metier => values.selectedMetier.includes(metier.id))}
                            onChange={(event, newValue) => {
                                const selectedMetierIds = newValue?.map(metier => metier.id);
                                setFieldValue('selectedMetier', selectedMetierIds);
                            }}
                            onBlur={handleBlur}
                            onOpen={fetchMetiers}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className='addUser_InputField'
                                    error={touched.selectedMetier && Boolean(errors.selectedMetier)}
                                    helperText={touched.selectedMetier && errors.selectedMetier}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: {
                                            minHeight: 'fit-content', // Adjust the minimum height as needed
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </DialogContent>

                <DialogActions className='addUser_dialog_actions'>
                    <Button className='addUser_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addUser_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};